import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { fmt } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function EditFees() {
  const { studentId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [discountHeads, setDiscountHeads] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!studentId) return;
    setData(null);
    api.get("/settings").then((r) => setDiscountHeads(
      (r.data?.discount_heads || []).map((h) => (typeof h === "string" ? { name: h, percent: 0, fixed: 0 } : { name: h.name || "", percent: h.percent || 0, fixed: h.fixed || 0 }))
    )).catch(() => {});
    api.get(`/students/${studentId}/fee-structure`).then((r) => {
      setData(r.data);
      setItems(r.data.items.map((i) => {
        const mult = i.mult || 1;
        return {
          ...i, mult,
          amount: Number(i.amount || 0) * mult,
          discount: Number(i.discount || 0) * mult,
          default_amount: Number(i.default_amount || 0) * mult,
          paid: Number(i.paid || 0),
          discount_head: i.discount_head || "",
          remark: i.remark || "",
        };
      }));
    }).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  }, [studentId]);

  const setField = (idx, key, v) => setItems((arr) => arr.map((it, i) => {
    if (i !== idx) return it;
    const next = { ...it, [key]: v };
    if (key === "discount_head") {
      const head = discountHeads.find((h) => h.name === v);
      if (head) {
        const amt = Number(next.amount || 0);
        const suggested = Number(head.percent) > 0 ? (amt * Number(head.percent)) / 100 : Number(head.fixed || 0);
        if (suggested > 0) next.discount = suggested;
      }
    }
    const maxDisc = Math.max(0, Number(next.amount || 0) - Math.round(Number(next.paid || 0)));
    if (Number(next.discount || 0) > maxDisc) next.discount = maxDisc;
    return next;
  }));

  const totals = items.reduce((a, it) => {
    const amt = Number(it.amount || 0), disc = Number(it.discount || 0), paid = Math.round(Number(it.paid || 0));
    return { amount: a.amount + amt, discount: a.discount + disc, paid: a.paid + paid, payable: a.payable + (amt - disc) };
  }, { amount: 0, discount: 0, paid: 0, payable: 0 });

  const save = async () => {
    const overrides = {}, discounts = {}, discount_heads = {}, remarks = {};
    items.forEach((it) => {
      const mult = it.mult || 1;
      if (Number(it.amount) !== Number(it.default_amount)) overrides[it.fee_name] = Number(it.amount || 0) / mult;
      const maxDisc = Math.max(0, Number(it.amount || 0) - Number(it.paid || 0));
      const clampedDisc = Math.min(Number(it.discount || 0), maxDisc);
      const unitDisc = clampedDisc / mult;
      if (unitDisc > 0) discounts[it.fee_name] = unitDisc;
      if (it.discount_head) discount_heads[it.fee_name] = it.discount_head;
      if ((it.remark || "").trim()) remarks[it.fee_name] = it.remark.trim();
    });
    setSaving(true);
    try {
      await api.put(`/students/${studentId}/fee-structure`, { overrides, discounts, discount_heads, remarks });
      toast.success("Fees updated");
      nav(-1);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-6xl mx-auto pb-16" data-testid="edit-fees-page">
      <div className="bg-white border-b border-slate-200 py-3 flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} data-testid="edit-fees-back-btn" className="rounded-full h-10 w-10"><ArrowLeft size={22} /></Button>
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-slate-900">Edit Fees</h1>
            {data && <p className="text-sm text-slate-500">{data.student_name}</p>}
          </div>
        </div>
        {data?.has_structure && (
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-fees-btn">
            {saving ? <Loader2 className="animate-spin" /> : "Save Fees"}
          </Button>
        )}
      </div>

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-indigo-500" /></div>
      ) : !data.has_structure ? (
        <div className="py-16 text-center text-slate-400">No fee structure set for this student's class/session.</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="edit-fees-table">
              <thead>
                <tr className="text-xs uppercase text-slate-400 font-semibold border-b border-slate-200">
                  <th className="text-left py-2 pr-3">Fee Head</th>
                  <th className="text-right py-2 px-3 w-32">Amount (Year)</th>
                  <th className="text-left py-2 px-3 w-44">Discount Head</th>
                  <th className="text-right py-2 px-3 w-32">Amount</th>
                  <th className="text-right py-2 px-3 w-28">Paid</th>
                  <th className="text-right py-2 px-3 w-28">Payable</th>
                  <th className="text-left py-2 pl-3 w-48">Remark</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const payable = Number(it.amount || 0) - Number(it.discount || 0);
                  const maxDisc = Math.max(0, Number(it.amount || 0) - Math.round(Number(it.paid || 0)));
                  return (
                    <tr key={it.fee_name} className="border-b border-slate-100">
                      <td className="py-2 pr-3">
                        <div className="font-medium text-slate-700">{it.fee_name}</div>
                        <div className="text-[11px] text-slate-400">{it.frequency}</div>
                      </td>
                      <td className="py-2 px-3">
                        <Input type="number" value={it.amount} onChange={(e) => setField(idx, "amount", e.target.value)} className="h-9 text-right tabular" data-testid={`fee-amount-${it.fee_name}`} />
                      </td>
                      <td className="py-2 px-3">
                        <Select value={it.discount_head || "none"} onValueChange={(v) => setField(idx, "discount_head", v === "none" ? "" : v)}>
                          <SelectTrigger className="h-9" data-testid={`fee-discount-head-${it.fee_name}`}><SelectValue placeholder="Select head" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— None —</SelectItem>
                            {discountHeads.map((hh) => <SelectItem key={hh.name} value={hh.name}>{hh.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 px-3">
                        <Input type="number" min={0} max={maxDisc} value={it.discount} onChange={(e) => setField(idx, "discount", e.target.value)} className="h-9 text-right tabular text-amber-600" data-testid={`fee-discount-${it.fee_name}`} title={Number(it.paid) > 0 ? `Max discount ${fmt(maxDisc)} (Amount − Paid)` : undefined} />
                      </td>
                      <td className="py-2 px-3 text-right font-semibold text-emerald-700 tabular" data-testid={`fee-paid-${it.fee_name}`}>{fmt(Math.round(Number(it.paid || 0)))}</td>
                      <td className="py-2 px-3 text-right font-semibold text-slate-800 tabular" data-testid={`fee-payable-${it.fee_name}`}>{fmt(payable)}</td>
                      <td className="py-2 pl-3">
                        <Input value={it.remark} onChange={(e) => setField(idx, "remark", e.target.value)} placeholder="Remark…" className="h-9" data-testid={`fee-remark-${it.fee_name}`} />
                      </td>
                    </tr>
                  );
                })}
                <tr className="font-bold text-sm">
                  <td className="py-2 pr-3 text-slate-700">Total (year)</td>
                  <td className="py-2 px-3 text-right tabular">{fmt(totals.amount)}</td>
                  <td className="py-2 px-3"></td>
                  <td className="py-2 px-3 text-right tabular text-amber-600" data-testid="fee-total-discount">{fmt(totals.discount)}</td>
                  <td className="py-2 px-3 text-right tabular text-emerald-700" data-testid="fee-total-paid">{fmt(totals.paid)}</td>
                  <td className="py-2 px-3 text-right tabular text-emerald-700">{fmt(totals.payable)}</td>
                  <td className="py-2 pl-3"></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400 mt-4">Amounts shown are the full year's total per head. Paid is read-only. Discount for a head is capped at Amount − Paid. Discounts entered here apply only to this student and reflect in the Discount column everywhere.</p>
        </div>
      )}
    </div>
  );
}
